import React, { FC, useState, useEffect } from "react";
import { DateRangePicker } from "react-date-range";
import {
  addDays, differenceInCalendarDays, format, startOfDay, endOfDay, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, subWeeks, subMonths, subYears
} from "date-fns";
import { enUS } from 'date-fns/locale';
import { toast } from "react-toastify";

type AppDateFilterProps = {
  isVisible: boolean;
  handleClose: () => void;
  onPickRange: (range: { startDate: string; endDate: string; label: string }) => void;
  initialRange?: { startDate: string; endDate: string; label: string };
};

const AppDatePickerWithCustomOptions: FC<AppDateFilterProps> = ({
  isVisible,
  handleClose,
  onPickRange,
  initialRange
}) => {

  const [showCalendar, setShowCalendar] = useState(false);
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  const defaultLast30 = {
    startDate: startOfDay(subDays(new Date(), 30)),
    endDate: endOfDay(new Date())
  };

  const [selectedRange, setSelectedRange] = useState([
    initialRange
      ? { startDate: new Date(initialRange.startDate), endDate: new Date(initialRange.endDate), key: "selection" }
      : { ...defaultLast30, key: "selection" }
  ]);
  const [selectedLabel, setSelectedLabel] = useState(initialRange?.label || "Last 30 Days");

  const presets = [
    {
      label: "Today", range: () => ({
        startDate: startOfDay(new Date()),
        endDate: endOfDay(new Date())
      })
    },

    {
      label: "Yesterday", range: () => {
        const d = subDays(new Date(), 1);
        return { startDate: startOfDay(d), endDate: endOfDay(d) };
      }
    },

    {
      label: "This Week", range: () => ({
        startDate: startOfWeek(new Date()),
        endDate: endOfWeek(new Date())
      })
    },

    {
      label: "Last Week", range: () => {
        const lastWeek = subWeeks(new Date(), 1);
        return { startDate: startOfWeek(lastWeek), endDate: endOfWeek(lastWeek) };
      }
    },

    // ✔ FIXED to include EXACT 7 days (today + 6 previous days)
    {
      label: "Last 7 Days", range: () => ({
        startDate: startOfDay(subDays(new Date(), 6)),
        endDate: endOfDay(new Date())
      })
    },

    // ✔ FIXED to include EXACT 15 days
    {
      label: "Last 15 Days", range: () => ({
        startDate: startOfDay(subDays(new Date(), 14)),
        endDate: endOfDay(new Date())
      })
    },

    // ✔ FIXED to include EXACT 30 days
    {
      label: "Last 30 Days", range: () => ({
        startDate: startOfDay(subDays(new Date(), 29)),
        endDate: endOfDay(new Date())
      })
    },

    {
      label: "This Month", range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date())
      })
    },

    {
      label: "Last Month", range: () => {
        const lastMonth = subMonths(new Date(), 1);
        return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
      }
    },

    {
      label: "This Year", range: () => ({
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date())
      })
    },

    {
      label: "Last Year", range: () => {
        const lastYear = subYears(new Date(), 1);
        return { startDate: startOfYear(lastYear), endDate: endOfYear(lastYear) };
      }
    },
  ];

  useEffect(() => {
    if (initialRange) {
      setSelectedLabel(initialRange.label);
      setSelectedRange([{
        startDate: new Date(initialRange.startDate),
        endDate: new Date(initialRange.endDate),
        key: "selection"
      }]);
    }
  }, [initialRange]);

  if (!isVisible) return null;

  const applyRange = () => {
    const { startDate, endDate } = selectedRange[0];

    onPickRange({
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      label: 'Custom'
    });

    setShowCalendar(false);
    handleClose();
  };

  const handlePresetClick = (p: any) => {
    const r = p.range();

    setSelectedLabel(p.label);
    setSelectedRange([{ ...r, key: "selection" }]);

    onPickRange({
      startDate: format(r.startDate, 'yyyy-MM-dd'),
      endDate: format(r.endDate, 'yyyy-MM-dd'),
      label: p.label
    });

    setShowCalendar(false);
    handleClose();
  };

  const closeCalendar = () => {
    setShowCalendar(false);
    handleClose();
  };

  return (
    <div className="position-absolute bg-white shadow border rounded" style={{ width: showCalendar ? 550 : 180, zIndex: 9999 }}>
      <div className="d-flex">

        {/* LEFT SIDE OPTIONS */}
        <div className="border-end" style={{ width: 180, maxHeight: showCalendar ? 400 : 300, overflowY: "auto" }}>
          {presets.map((p, i) => (
            <div
              key={i}
              onClick={() => handlePresetClick(p)}
              onMouseEnter={() => setHoveredLabel(p.label)}
              onMouseLeave={() => setHoveredLabel(null)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderRadius: "4px",
                backgroundColor:
                  selectedLabel === p.label
                    ? "#0d6efd"        
                    : hoveredLabel === p.label
                      ? "#f1f3f5"          
                      : "transparent",
                color: selectedLabel === p.label ? "#fff" : "#212529",
                transition: "background-color 0.15s ease",
              }}
            >
              {p.label}
            </div>
          ))}

          {/* CUSTOM BUTTON */}
          <div
            onMouseEnter={() => setHoveredLabel("Custom")}
            onMouseLeave={() => setHoveredLabel(null)}
            onClick={() => {
              setShowCalendar(true);
              setSelectedLabel("Custom");
            }}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              fontWeight: 600,
              borderRadius: "4px",
              backgroundColor:
                selectedLabel === "Custom"
                  ? "#0d6efd"
                  : hoveredLabel === "Custom"
                    ? "#f1f3f5"
                    : "transparent",
              color: selectedLabel === "Custom" ? "#fff" : "#0d6efd",
              transition: "background-color 0.15s ease",
            }}
          >
            Custom
          </div>
        </div>

        {/* RIGHT SIDE CALENDAR */}
        {showCalendar && (
          <div className="p-2">
            <DateRangePicker
              ranges={selectedRange}
              locale={enUS}
              maxDate={new Date()}
              moveRangeOnFirstSelection={false}
              onChange={(range: any) => {
                const { startDate, endDate } = range.selection;

                if (!startDate || !endDate) return;

                const daysSelected =
                  differenceInCalendarDays(endDate, startDate) + 1;

                if (daysSelected > 45) {
                  toast.error("You can select a maximum of 45 days only", {
                    toastId: "max-45-days", // prevents duplicate spam
                  });
                  return;
                }

                setSelectedRange([range.selection]);
              }}

              rangeColors={["#0d6efd"]}
              className="custom-date-range-picker"
            />

            <style>{`
              .custom-date-range-picker .rdrDefinedRangesWrapper {
                display: none !important;
              }
            `}</style>

            <div className="d-flex justify-content-end gap-2 mt-2">
              <button className="btn btn-light btn-sm" onClick={closeCalendar}>Close</button>
              <button className="btn btn-primary btn-sm" onClick={applyRange}>Apply</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AppDatePickerWithCustomOptions;
